<template>
  <b-row>
    <b-col>
        <h4 class="d-inline mr-3">
          {{ endpoint.id ? endpoint.name : 'New Endpoint' }}
        </h4>
      <template v-if="eid">
        <toggle-button
          v-model="endpoint.enabled"
          @change="setEndpoint()"
          :sync="true"
          :labels="{ checked: 'Enabled', unchecked: 'Disabled' }"
          :height="26"
          :width="80"
          class="align-middle"
          :color="{ checked: '#2bd879', unchecked: '#F1516C' }">
        </toggle-button>
      </template>
      <transition name="fade">
        <b-card v-if="this.endpoint && this.endpoint.id && this.endpoint.qps > 0" title="Adaptive QPS Settings" class="mb-4 bg-light">
          <b-alert show>
            Adaptive QPS control enables curve fitting of request volumes to better align auction quota
            during times of high bidding value while anchoring towards a daily average volume
          </b-alert>
          <b-row>
            <b-col lg="5" xl="3">
              <b-form-row v-for="hour of Object.keys(endpoint.qps_control_points).sort((a, b) => Number(a) - Number(b))" :key="hour">
                    <b-col cols="10">
                      <b-form-group>
                        <b-input-group size="sm">
                          <b-input-group-text slot="prepend">
                            Hour {{ hour < 10 ? '0' + hour : hour }}
                          </b-input-group-text>
                          <b-form-input
                            :disabled="!isSuperAdmin"
                            v-model.number="endpoint.qps_control_points[hour]"
                            type="number"
                            max="2"
                            min="0.25"
                            :state="endpoint.qps_control_points[hour] >= 0.25 && endpoint.qps_control_points[hour] <= 2"
                            placeholder="Amplitude"
                            required>
                          </b-form-input>
                          <b-input-group-text slot="append" class="d-sm-none d-sm-flex">
                            Amplitude
                          </b-input-group-text>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col cols="1">
                      <b-btn :disabled="hour == 0 || hour == 24 || !isSuperAdmin" class="d-sm-none d-md-flex"
                        size="sm" @click="delCtrlPoint(hour)" variant="outline-danger">x</b-btn>
                    </b-col>
              </b-form-row>
              <b-row>
                <b-col lg="8" class="mt-4">
                  <b-form-group label="New Hour Control Point">
                    <b-input-group>
                      <b-input-group-text slot="prepend">
                        Hour
                      </b-input-group-text>
                      <b-form-input
                        v-model="tmpHour"
                        :disabled="!isSuperAdmin"
                        placeholder="Hour Index (0-24)"
                        required>
                      </b-form-input>
                    </b-input-group>
                  </b-form-group>
                  <b-btn size="sm" :disabled="!this.tmpHour || endpoint.qps_control_points.length == 25 || !isSuperAdmin"
                         variant="primary" @click="addCtrlPoint">Add Control Point</b-btn>
                </b-col>
              </b-row>
            </b-col>
            <b-col lg="7" xl="9">
              <qps-graph :points="computedQpsPoints" :qps="endpoint.qps"></qps-graph>
            </b-col>
          </b-row>
        </b-card>
      </transition>
      <b-card class="my-2">
        <h5 class="my-4">General Settings</h5>
        <b-form-row>
          <b-col md="4">
            <b-form-group
              label="Endpoint Name">
              <b-form-input
                v-model="endpoint.name"
                placeholder="Reference name"
                type="text"
                required>
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group
              label="Endpoint Location">
              <b-select
                v-model="endpoint.datacenter"
                :options="dcOptions">
              </b-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group
              label="Endpoint QPS Limit">
                <b-form-input
                  v-model.number="endpoint.qps"
                  type="number"
                  required>
                </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group
              label="Floor Markup">
              <b-form-input
                v-model="endpoint.floor_markup"
                type="number">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group
              label="GZIP Requests">
              <toggle-button
                v-model="endpoint.gzip"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isAdmin">
          <b-form-group
            label="Endpoint is vast">
            <toggle-button
              v-model="tmpVastShow"
              :sync="true"
              color="#2bd879">
            </toggle-button>
          </b-form-group>
        </b-form-row>
        <b-form-row v-if="!isVast">
          <b-col md="5">
            <b-form-group v-if="!isVast"
              label="Endpoint">
              <b-input-group>
                <b-input-group-text slot="prepend">
                  URL
                </b-input-group-text>
                <b-form-input
                  v-model="endpoint.url"
                  placeholder="HTTP endpoint destination"
                  required>
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="hop Limit">
              <b-form-select v-model="endpoint.hop_limit">
                <option :value="1">1</option>
                <option :value="2">2</option>
                <option :value="3">3</option>
                <option :value="4">4</option>
                <option :value="0">5</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Imp Limit">
              <b-form-input v-model="endpoint.max_imp_len" />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="TRO Width">
              <b-form-input
                :disabled="!isSuperAdmin"
                v-model="endpoint.take_rate_explore_width"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group
              label="Bill On View">
              <toggle-button
                v-model="endpoint.bill_on_view"
                :disabled="!isSuperAdmin"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group
              label="Store All Auctions (Careful!!)">
              <toggle-button
                v-model="endpoint.store_all_requests"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isVast">
          <b-col>
            <b-form-group v-if="isVast" label="VAST Settings">
              <b-form-textarea v-model="endpoint.vast.vast" />
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row v-if="isVast">
          <b-col lg="3">
            <b-form-group label="Tag Campaign Id">
              <b-form-input v-model="endpoint.vast.cid" />
            </b-form-group>
          </b-col>
          <b-col lg="3">
            <b-form-group label="Tag Creative Id">
              <b-form-input v-model="endpoint.vast.crid" />
            </b-form-group>
          </b-col>
          <b-col lg="3">
            <b-form-group label="Bid Price">
              <b-form-input type="number" v-model="endpoint.vast.price" />
            </b-form-group>
          </b-col>
          <b-col lg="3">
            <b-form-group label="Network precheck">
              <toggle-button
                v-model="endpoint.vast.precheck"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>

        <div class="mb-4">
          <h5 class="mt-4">
            Traffic Shaping
          </h5>
          <span class="text-muted small">
            <span v-if="endpoint.throttle_type === 'NONE'">No request throttling enabled</span>
            <span v-else-if="endpoint.throttle_type === 'PLACEMENT'">Conservative throttling enabled, activated by any bidding activity</span>
            <span v-else-if="endpoint.throttle_type === 'SMART_RPM'">Smart throttling enabled, will prioritize segments by revenue per 1mm auctions</span>
            <span v-else-if="endpoint.throttle_type === 'TREE_RPM'">Sequoia (Tree) throttling enabled, will prioritize segments by revenue per 1mm auctions</span>
            <span v-else-if="endpoint.throttle_type === 'FUZZY_RPM'">Smart bucketing throttle enabled, will prioritize segments by revenue per 1mm auctions</span>
            <span v-else-if="endpoint.throttle_type === 'FUZZY_NRPM'">Smart bucketing throttle enabled, will prioritize segments by <b>net</b> revenue per 1mm auctions</span>
            <span v-else-if="endpoint.throttle_type === 'SMART_FILLRATE'">Smart throttling enabled, will prioritize segments by fillrate (clears/auctions)</span>
            <span v-else-if="endpoint.throttle_type === 'TREE_FILL'">Sequoia (Tree) throttling enabled, will prioritize segments by fillrate (clears/auctions)</span>
            <span v-else-if="endpoint.throttle_type === 'FUZZY_FILL'">Smart bucketing throttle enabled, will prioritize segments by fillrate (clears/auctions)</span>
            <span v-else-if="endpoint.throttle_type === 'FUZZY_BIDRATE'">Smart bucketing throttle enabled, will prioritize segments by bidrate (bids/auctions)</span>
            <span v-else-if="endpoint.throttle_type === 'SMART_BIDRATE'">Smart throttling enabled, will prioritize segments by bidrate (bids/auctions)</span>
            <span v-else-if="endpoint.throttle_type === 'SMART_BIDS'">Smart throttling enabled, will prioritize segments by quantity of bids</span>
          </span>
        </div>

        <b-form-row>
          <b-col lg="2">
            <b-form-group
              label="Smart Request Throttling"
              description="Throttle implementation and priority">
              <b-form-select v-model="endpoint.throttle_type" @change="updateThrottleDefault">
                <option value="NONE">Disable Throttle</option>
                <option value="PLACEMENT">Any Bid (Original)</option>
                <option value="SMART_RPM">Smart Throttle (RPM)</option>
                <option value="SMART_FILLRATE">Smart Throttle (Fillrate)</option>
                <option value="SMART_BIDRATE">Smart Throttle (Bidrate)</option>
                <option value="SMART_BIDS">Smart Throttle (Has Bids)</option>
                <option value="TREE_CLEARS">Tree Throttle (Has Clears)</option>
                <option value="TREE_BIDRATE">Tree Throttle (Bidrate)</option>
                <option value="TREE_RPM">Tree Throttle (RPM)</option>
                <option value="TREE_FILL">Tree Throttle (Fillrate)</option>
                <option value="FUZZY_RPM">Tree (Fuzzy) Throttle (RPM)</option>
                <option value="FUZZY_NRPM">Tree (Fuzzy) Throttle (nRPM)</option>
                <option value="FUZZY_BIDRATE">Tree (Fuzzy) Throttle (Bidrate)</option>
                <option value="FUZZY_FILL">Tree (Fuzzy) Throttle (Fillrate)</option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type !== 'NONE'">
            <b-form-group label="Exploratory Reserve"
                          description="% of inventory to send as testing">
              <b-input-group prepend="%" :append="endpoint.qps > 0 ? (Math.round(endpoint.qps * (endpoint.throttle_explorer_value/100)) + ' qps') : 'Of All'">
                <b-form-input v-model="endpoint.throttle_explorer_value" />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type !== 'NONE'">
            <b-form-group label="Time Window"
                          description="Minutes of decisioning lookback">
              <b-input-group prepend="Minutes">
                <b-form-input v-model="endpoint.throttle_window" />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col v-if="endpoint.throttle_type !== 'NONE' && endpoint.throttle_type !== 'PLACEMENT'">
            <b-form-group label="Min Auctions" description="Min auctions per segment before throttling">
              <b-input-group prepend="Auctions">
                <b-form-input v-model="endpoint.throttle_min_auctions" />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type !== 'PLACEMENT' && endpoint.throttle_type !== 'NONE'">
            <b-form-group
              :label="(endpoint.throttle_type.indexOf('PRIORITY') === -1 ? 'Target' : 'Minimum') + ' Metric Value'"
              description="Per segment metric to optimize towards">
              <b-input-group :prepend="throttleSymbol">
                <b-form-input v-model="endpoint.throttle_target_value" />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type !== 'PLACEMENT' && endpoint.throttle_type !== 'NONE'">
            <b-form-group label="Boost Mode" description="Accelerate learning sending unoptimized QPS">
              <toggle-button
                v-model="endpoint.throttle_boost"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-row v-if="endpoint.throttle_type !== 'NONE'">
          <b-col lg="6">
            <b-form-group
              label="Traffic Shaping Features"
              description="Unique combination of fields used for decisioning on activity. Empty selection falls back to original default feature list.">
              <mulselect
                variant="light-muted"
                :options="featureOptions"
                option-value-field="value"
                option-label-field="name"
                :values="endpoint.throttle_features">
              </mulselect>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type.indexOf('TREE') === 0 || endpoint.throttle_type.indexOf('FUZZY') === 0">
            <b-form-group label="Optimize Metric" description="Dynamically adjust metric to best utilize QPS">
              <toggle-button
                v-model="endpoint.throttle_auto_metric"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>

          <b-col lg="2" v-if="endpoint.throttle_type.indexOf('TREE') === 0 || endpoint.throttle_type.indexOf('FUZZY') === 0">
            <b-form-group label="Boost Level" description="Depth of features to accelerate learning to">
              <b-input-group prepend="Depth">
                <b-form-input v-model="endpoint.throttle_boost_level" type="number"
                              min="1" :max="endpoint.throttle_features.length"
                              :state="endpoint.throttle_boost_level > 0 && endpoint.throttle_boost_level <= endpoint.throttle_features.length"/>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col v-if="endpoint.throttle_type.indexOf('FUZZY') === 0">
            <b-form-group disabled label="Autotune" description="(Beta) Auto-tune feature order">
              <toggle-button
                :disabled="!isSuperAdmin"
                v-model="endpoint.throttle_autotune"
                :sync="true"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">
          Ad Network Options
          <b-btn class="btn-sm mt-auto" @click="endpoint.exchanges = []">Clear</b-btn>
          <b-btn class="btn-sm btn-primary mt-auto" @click="addAllExchanges()">Enable All</b-btn>
        </h5>
        <b-form-row>
          <b-col md="12">
            <b-form-group
              label="Networks"
              :description="BRAND + ' network is a catch-all for directly integrated publishers'">
              <mulselect
                variant="light-muted"
                :options="exchangeOptions"
                option-value-field="company_id"
                option-label-field="name"
                :values="endpoint.exchanges">
              </mulselect>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Platforms</h5>
        <b-form-row>
          <b-col>
            <b-alert>Platform targeting has been deprecated</b-alert>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Ad Formats</h5>
        <b-form-row>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.banner">
                <small>Banner</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.video">
                <small>Video</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.native">
                <small>Native</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">
          Countries
          <b-btn class="btn-sm ml-3" @click="endpoint.countries = []">Clear</b-btn>
        </h5>
        <b-form-row>
          <b-col md="12">
            <b-form-group
              label="Countries">
              <many-selector
                :options="countryOptions"
                :values="endpoint.countries"
                value-name-plural="countries"
                placeholder="Select Country"
                select-all-text="All Countries">
              </many-selector>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Request Filters</h5>
        <b-form-row>
          <b-col>
            <b-form-group label="Direct Billing Only"
                          description="Only receive requests you have a direct billing relationship with">
              <toggle-button
                v-model="endpoint.direct_only"
                :sync="true"
                class="align-middle"
                color="#2bd879">
              </toggle-button>
            </b-form-group>
          </b-col>
        </b-form-row>
        <cool-label>Field targeting</cool-label>
        <b-form-row>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.not_lat_long">
                <small>Require lat/long</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.gps_only">
                <small>GPS Only Data</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.apps">
                <small>Block App Requests</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.sites">
                <small>Block Site Requests</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
        </b-form-row>
        <cool-label>Blocked Device Types</cool-label>
        <b-form-row>
          <b-col md="2">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.desktop">
                <small>Desktop</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.mobile">
                <small>Smartphone</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.tablet">
                <small>Tablet</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.settop">
                <small>Set-top (Roku, Firestick..)</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group>
              <b-checkbox
                v-model="endpoint.filters.ctv">
                <small>Connected TV</small>
              </b-checkbox>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col md="3">
            <b-form-group
              label="Publisher ID Filter">
              <b-select
                v-model="endpoint.filters.pub_filter.type"
                :options="listTypeOptions">
              </b-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Clear IDs" v-if="endpoint.filters.pub_filter.pub_ids.length">
              <b-btn class="btn-sm mt-1"
                     @click="endpoint.filters.pub_filter.pub_ids = []">Clear</b-btn>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col md="12">
            <b-form-group label="List of publisher ids">
              <string-list-editor :values="endpoint.filters.pub_filter.pub_ids"
                                  placeholder="Enter publisher ID"></string-list-editor>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Domain Targeting</h5>
        <b-form-row>
          <b-col md="3">
            <b-form-group
              label="Filter Type">
              <b-select
                v-model="endpoint.filters.domain_filter.type"
                :options="listTypeOptions">
              </b-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Clear IDs" v-if="endpoint.filters.domain_filter.domains.length">
              <b-btn
                    class="btn-sm mt-1" @click.prevent="endpoint.filters.domain_filter.domains = []">Clear</b-btn>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Edit domain list">
              <string-list-editor :values="endpoint.filters.domain_filter.domains"
                                  placeholder="Enter domain"></string-list-editor>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Frequency Capping</h5>
        <b-form-row>
          <b-col lg="4">
            <b-form-group label="Minutes between cap"
            description="Minutes between events per unique id (idfa, uid, or IP). 0 is no frequency cap.">
              <b-input v-model="endpoint.freq_cap_mins"></b-input>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Cap Type"
                          description="Cap applies to bid requests or cleared impressions?">
              <b-form-select v-model="endpoint.freq_cap_by_reqs" :options="freqCapOpts"></b-form-select>
            </b-form-group>
          </b-col>
        </b-form-row>

        <h5 class="my-4">Request filters</h5>
        <b-form-row>
          <b-col lg="4">
            <b-form-group label="Max bid floor"
                          description="Filter requests with all imps above this bid floor ($)">
              <b-input v-model="endpoint.max_floor"></b-input>
            </b-form-group>
          </b-col>
          <b-col lg="4">
            <b-form-group label="Min Tmax"
                          description="Filter requests with tmax below this value (ms)">
              <b-input v-model="endpoint.min_tmax"></b-input>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col md="12">
            <b-form-group
              label="Device ID Targeting"
              description="Target only users in selected device ID lists. Contact your account manager for assistance.">
              <mulselect
                variant="light-muted"
                :options="formattedUserListOptions"
                :values="endpoint.user_lists">
              </mulselect>
            </b-form-group>
          </b-col>
        </b-form-row>
      </b-card>

      <b-btn @click="setEndpoint(true)" variant="primary" class="float-right">
        {{ eid ? 'Save Changes' : 'Create'}}
      </b-btn>
    </b-col>
  </b-row>
</template>

<script>
  import auth from '@/auth'
  import { MiscApi } from "@/api";
  import { ExchangeApi } from '@/exchange/api'
  import { DemandApi } from "@/demand/api";
  import StringListEditor from "@/components/StringListEditor"
  import ManySelector from "@/components/ManySelector"
  import Mulselect from '@/components/Mulselect'
  import { stateMerge } from 'vue-object-merge'
  import QpsGraph from "./components/QpsGraph.vue";

  export default {
      mounted () {
          if (this.eid) {
              DemandApi.endpoints(this.cid).then((l) => {
                  for(let ep of l) {
                      if (ep.id === this.eid) {
                          this.originalExchanges.push(...ep.exchanges)
                          const newEp = { ...this.endpoint }
                          stateMerge(newEp, ep)
                         // merge fucks this up some how
                          if (ep.qps_control_points)
                            newEp.qps_control_points = ep.qps_control_points
                          else
                            newEp.qps_control_points = { "0": 1, "24": 1 }

                          if (newEp['take_rate_explore_width'] == undefined)
                            newEp.take_rate_explore_width = 0;

                          this.endpoint = newEp;

                          setTimeout(() => {
                            this.tmpVastShow = this.isVast
                          })

                          return
                      }
                  }
                  this.$notify.error("Not Found", "Uneable to find given endpoint ID")
              })
          }

          this.getCountryList()
          this.getExchangeList()
          this.getUserLists()
      },
      props: {
          cid: { required: true },
          eid: { required: false }
      },
      data () {
          return {
              isSuperAdmin: auth.isSuperAdmin(),
              tmpHour: null,
              BRAND,
              tmpVastShow: false, /* tmp flag to show the vast entry box */
              endpoint: this.defaultEp(),
              originalExchanges: [], // enabled exchanges at page load
              dcOptions: [
                {value: 'us-east', text: 'US-East'},
                {value: 'us-west', text: 'US-West'},
                {value: 'eu-west', text: 'Europe'}
              ],
              listTypeOptions: [
                  {value: 'BLACKLIST', text: 'Blacklist'},
                  {value: 'WHITELIST', text: 'Whitelist'}
              ],
              countryOptions: [],
              exchangeOptions: [],
              freqCapOpts: [
                { value: false, text: 'Impressions' },
                { value: true, text: 'Requests' }
              ],
              userListOptions: [],
            featureOptions: [
              { value: 'GEO', name: 'Country' },
              { value: 'FORMAT', name: 'Ad Format' },
              { value: 'DEVTYPE', name: 'Device Type' },
              { value: 'OS', name: 'OS' },
              { value: 'COMPANYID', name: 'Pub Id' },
              { value: 'PUB', name: 'Pub Domain' },
              { value: 'PUBID', name: '(Beta) Ext Pub Id' },
              { value: 'DOMAIN', name: 'Domain/Bundle' },
              { value: 'SITEID', name: 'App/Site Id' },
              { value: 'ZONEID', name: 'Zone Id' },
              { value: 'SIZE', name: 'Ad Size' },
              { value: 'DEVCON', name: 'Dev Connection' },
              { value: 'LMT', name: 'Do Not Track' },
              { value: 'VDUR', name: 'Video Duration' },
              { value: 'GEOTYPE', name: 'Location Type' },
              { value: 'PLCMT', name: 'Plcmt' }
            ]
          }
      },
      methods: {
        delCtrlPoint (hour) {
          this.$delete(this.endpoint.qps_control_points, hour)
        },
        addCtrlPoint () {
          console.log('click')
          const hour = this.tmpHour
          if (hour == undefined || this.endpoint.qps_control_points[hour])
            return this.$notify.error("Hour is undefined or already exists")

          this.$set(this.endpoint.qps_control_points, hour, 1)
          this.tmpHour = null
        },
        addAllExchanges () {
          let list = []

          for(let ex of this.exchangeOptions) {
            if (!ex.restricted) {
              list.push(ex.company_id)
            }

            this.endpoint.exchanges = list
          }
        },
        exchangeById (id) {
          for (let ex of this.exchangeOptions) {
            if (ex.company_id === id) {
              return ex
            }
          }
        },
        defaultEp() {
          return {
            bill_on_view: false,
            company_id: this.cid,
            enabled: true,
            datacenter: 'us-east',
            gzip: false,
            android: true,
            ios: true,
            windows: true,
            roku: false,
            apple_tv: false,
            tizen: false,
            web_os: false,
            banner: true,
            os_other: false,
            countries: [],
            qps: 500,
            qps_control_points: {},
            exchanges: [],
            direct_only: false,
            freq_cap_mins: 0,
            freq_cap_by_reqs: false,
            max_floor: 0,
            min_tmax: 40,
            throttle_type: 'PLACEMENT',
            throttle_boost: false,
            throttle_target_value: null,
            throttle_explorer_value: 10,
            throttle_min_auctions: 1000,
            throttle_window: 30,
            max_imp_len: 0,
            throttle_features: [],
            throttle_user_recall: false,
            throttle_autotune: false,
            floor_markup: 0,
            hop_limit: 0,
            vast: {
              /* maybe */
            },
            filters: {
              pub_filter: { pub_ids: [] },
              domain_filter: { domains: [] }
            },
            user_lists: []
          }
        },
          valid () {
              let ep = this.endpoint
            return ep &&
                (ep.url || ep.vast.vast) &&
                !isNaN(ep.qps) &&
                (ep.android || ep.ios || ep.windows || ep.os_other || ep.roku || ep.osx || ep.tizen || ep.apple_tv || ep.web_os) &&
                ep.countries && ep.countries.length &&
              ep.exchanges && ep.exchanges.length &&
                (ep.banner || ep.video || ep.native);
          },
          setEndpoint(redir) {
              if (!this.valid()) {
                return this.$notify.error('Check Fields', 'Please complete all fields')
              }

              DemandApi.endpoint(this.endpoint).then((r) => {
                  this.$notify.success('Endpoint settings updated')
                  if (redir === true) {
                      this.$router.go(-1)
                  }
              })
          },
          getCountryList() {
              MiscApi.getCountryList().then((data) => {
                let set = new Set(data.countries)
                console.log(set.keys())
                  this.countryOptions.push(...set.keys())
              }, (err) => {
                  console.warn(err)
              })
          },
          getExchangeList() {
              ExchangeApi.list().then((data) => {
                  this.exchangeOptions.push(...data)
              })
          },
          getUserLists () {
              DemandApi.userLists(this.cid).then((list) => {
                this.userListOptions.push(...list)
              })
          },
        updateThrottleDefault () {
          switch (this.endpoint.throttle_type) {
            case 'SMART_BIDRATE':
            case 'SMART_RPM':
              this.endpoint.throttle_target_value = 1;
              break
            case 'SMART_FILLRATE':
            case 'SMART_FILLRATE_PRIORITY':
            case 'SMART_RPM_PRIORITY':
              this.endpoint.throttle_target_value = 0.25;
              break
            case 'SMART_BIDS':
              this.endpoint.throttle_target_value = 10;
              break
            case 'PLACEMENT':
            case 'NONE':
              this.endpoint.throttle_target_value = 0
              break
            default:
              console.log('No default value for throttle ' + this.endpoint.throttle_type)
          }
        }
      },
    watch: {
        'endpoint.exchanges' (nv) {
          let ov = this.originalExchanges
          console.log(ov.length + ' <> ' + nv.length)
          let diff = nv.filter(x => !ov.includes(x))
          if (!diff.length) {
            console.log('no changes? ' + nv.length + " <> " + ov.length)
            return // weird flex but ok
          }

          let ex = this.exchangeById(diff[0])
          console.log(ex)
          if (ex.restricted && !auth.isAdmin()) {
            console.log('thats a no dog')
            this.$notify.error('Exchange is restricted, please ask for assistance')
            this.endpoint.exchanges = ov
          }
        },
    },
      computed: {
          computedQpsPoints () {
            if (!this.endpoint || !this.endpoint.qps)
              return { '0': 1, '24': 1 };

            const cps = this.endpoint.qps_control_points
            if (!cps || !Object.keys(cps).length)
              return { '0': 1, '24': 1 };

            return cps
          },
          throttleSymbol () {
            switch (this.endpoint.throttle_type) {
              case 'SMART_RPM':
              case 'SMART_RPM_PRIORITY':
                return '$'
              case 'SMART_BIDRATE':
              case 'SMART_FILLRATE':
              case 'SMART_FILLRATE_PRIORITY':
                return '%'
              case 'SMART_BIDS':
                return 'bids'
              default:
                return ''
            }
          },
          isVast () {
              return this.tmpVastShow || (this.endpoint.vast
                  && this.endpoint.vast.vast && this.endpoint.vast.vast.length)
          },
          isAdmin () {
              return auth.isAdmin()
          },
          formattedUserListOptions () {
            let list = []
            for (let en of this.userListOptions) {
              list.push({ value: en, label: en })
            }
            return list
          }
      },
      components: {
          QpsGraph,
          Mulselect,
          StringListEditor,
          ManySelector
      }
  }
</script>



// WEBPACK FOOTER //
// src/demand/pages/BidderEndpoint.vue